// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** Where data-protection-officer appointments live. */
export const DPO_APPOINTMENTS_COLLECTION = 'grc_dpo_appointments';
/** Where public privacy notices live. */
export const PRIVACY_NOTICES_COLLECTION = 'grc_privacy_notices';
// ── Publication ───────────────────────────────────────────────────
/**
 * Where something was made public.
 *
 * Platform vocabulary, deliberately: these are the kinds of place this product
 * can record, not a classification any installed content declares. A content
 * pack that wants to require one of them narrows on it through its own
 * applicability facets; it never gets to add a member here.
 */
export const PUBLICATION_CHANNELS = [
    'public_website',
    'mobile_app',
    'intranet',
    'physical_notice',
    'direct_notice',
    'other',
];
/**
 * Three states, and the third is not a shade of the first.
 *
 * `not_published` is "we have not said this publicly"; `withdrawn` is "we did,
 * and we have taken it down", which carries a reason and leaves the publication
 * history intact. Collapsing the two would make a notice that was live for a
 * year and pulled last week indistinguishable from one that never went out.
 */
export const PUBLICATION_STATES = ['not_published', 'published', 'withdrawn'];
// ── DPO-01: the appointment ───────────────────────────────────────
/**
 * The lifecycle of an appointment.
 *
 * `in_effect` is the state that changes the world: from it the organisation has
 * a named person in the post. `draft` is a record somebody is still entering,
 * and the two terminal states are different answers to "why is this person no
 * longer the officer" — an appointment that ran its course versus one replaced
 * by another. A single `inactive` would lose that, and "who was the officer on
 * the day of the incident" is exactly the question a register is read for.
 */
export const DPO_APPOINTMENT_STATUSES = ['draft', 'in_effect', 'ended', 'superseded'];
/**
 * The statuses in which the organisation actually has an appointed officer.
 *
 * A list rather than a comparison against one member, and for the reason
 * `CONTRACT_STATUSES_IN_FORCE` is: the trigger that raises the duty is computed
 * from this set on both sides, so widening the lifecycle later cannot open a
 * second way into effect that raises no duty.
 */
export const DPO_APPOINTMENT_STATUSES_IN_EFFECT = ['in_effect'];
/**
 * The legality table. Keyed by the union, so `tsc` refuses a status that has no
 * row and a row that has no status — the drift `VALID_STATUSES` in the contracts
 * module was hand-copied into before Sprint 65.5.0 derived it from here.
 */
export const DPO_APPOINTMENT_TRANSITIONS = {
    draft: ['in_effect'],
    in_effect: ['ended', 'superseded'],
    ended: [],
    superseded: [],
};
export function isDpoAppointmentTransitionAllowed(from, to) {
    return DPO_APPOINTMENT_TRANSITIONS[from].includes(to);
}
/** Does this transition put an officer in post who was not in post before? */
export function dpoAppointmentTakesEffect(from, to) {
    return (DPO_APPOINTMENT_STATUSES_IN_EFFECT.includes(to) &&
        !DPO_APPOINTMENT_STATUSES_IN_EFFECT.includes(from));
}
// ── PUB-01: the public privacy notice ─────────────────────────────
/**
 * The lifecycle of a public notice.
 *
 * `approved` and `published` are two states and not one, because they are two
 * acts by (usually) two different people: somebody with authority signs the text
 * off, and somebody else puts it where the public can read it. A single state
 * would make "approved but never actually published" — the failure this row is
 * most often found in — unrepresentable, and therefore invisible.
 */
export const PRIVACY_NOTICE_STATUSES = [
    'draft',
    'approved',
    'published',
    'withdrawn',
    'superseded',
];
/** The statuses in which a notice is actually public. */
export const PRIVACY_NOTICE_STATUSES_PUBLISHED = ['published'];
export const PRIVACY_NOTICE_TRANSITIONS = {
    // A rejected review goes back to drafting; there is no path from `draft`
    // straight to `published`, which is the whole content of the approval gate.
    draft: ['approved'],
    approved: ['published', 'draft'],
    published: ['withdrawn', 'superseded'],
    // A notice taken down may still be replaced later by the one that supersedes
    // it; a superseded notice is terminal.
    withdrawn: ['superseded'],
    superseded: [],
};
export function isPrivacyNoticeTransitionAllowed(from, to) {
    return PRIVACY_NOTICE_TRANSITIONS[from].includes(to);
}
/** Does this transition make a notice public that was not public before? */
export function privacyNoticeBecomesPublic(from, to) {
    return (PRIVACY_NOTICE_STATUSES_PUBLISHED.includes(to) &&
        !PRIVACY_NOTICE_STATUSES_PUBLISHED.includes(from));
}
// ── Audience, channel, date in effect and deployment (Sprint 62.58.0, AC-N1) ──
/**
 * Whom a notice addresses. `public` is the register's original meaning (the
 * notice anyone can read); `data_subject_group` names the data subjects of the
 * linked processing activities it is given to (drivers, passengers, staff).
 */
export const PRIVACY_NOTICE_AUDIENCE_KINDS = ['public', 'data_subject_group'];
/**
 * How a notice reaches its audience — a closed, instrument-neutral set
 * (AC-N1). An in-vehicle sticker or screen is `point_of_collection`. Distinct
 * from {@link PUBLICATION_CHANNELS}, which records where a PUBLISHED notice can
 * be found; this records how the audience is given the notice at all, and is
 * stated when the notice is drafted.
 */
export const PRIVACY_NOTICE_DELIVERY_CHANNELS = [
    'website',
    'point_of_collection',
    'employment_documentation',
    'contract_or_terms',
    'in_app',
    'email',
    'other',
];
/** The bounds the draft body enforces (AC-N1). */
export const PRIVACY_NOTICE_DEPLOYMENT_LIMITS = {
    /** The data subjects one notice may name. */
    audience_data_subjects: 100,
    /**
     * One data-subject label, trimmed. The activity form stores its
     * `data_subjects` unbounded, so this is a sane maximum rather than a second
     * rule a recorded label could fail (gate E12.5 pass 1, P3-2: it was 200, and
     * a longer label the activity records could not be named).
     */
    audience_data_subject: 2000,
    channel_note: 500,
    /** Processing activities one notice may be linked to. */
    processing_activity_ids: 100,
    /** Data-map systems one notice may be linked to. */
    data_map_system_ids: 100,
};
/**
 * The four fields the Sprint 62.58.0 migration gives a notice drafted before
 * them (AC-N1): `public`, `['website']`, the first publication date, and no
 * named data subjects. The migration writes exactly these and nothing else.
 */
export const PRIVACY_NOTICE_MIGRATED_FIELDS = [
    'audience_kind',
    'audience_data_subjects',
    'channels',
    'effective_from',
];
// ── Errors ────────────────────────────────────────────────────────
/**
 * The refusals this module makes, declared as a value.
 *
 * A code the API answers with is part of its contract, and a transcription of it
 * in a test or a client is a second spelling free to drift — the defect
 * `TASK_SOURCE_MODULES` was added to fix for its own union.
 */
export const PRIVACY_OFFICE_ERROR_CODES = [
    'DPO_APPOINTMENT_NOT_FOUND',
    'PRIVACY_NOTICE_NOT_FOUND',
    'INVALID_STATUS_TRANSITION',
    /** An appointment cannot take effect without a person and a decision behind it. */
    'APPOINTMENT_DECISION_INCOMPLETE',
    /**
     * Publishing an appointment that is not in effect would put a person's name
     * on a public page as the organisation's officer before the organisation says
     * they are one.
     */
    'APPOINTMENT_NOT_IN_EFFECT',
    /** A notice cannot be published without a recorded approval. */
    'NOTICE_APPROVAL_REQUIRED',
    /** Publishing with no target is a claim that something is public nowhere. */
    'PUBLICATION_TARGET_REQUIRED',
    /** Taking something down is a decision, and it carries its reason. */
    'WITHDRAWAL_REASON_REQUIRED',
    /**
     * Sprint 62.58.0 (AC-N1) — linking a notice to processing activities or
     * data-map systems reads those registers, so it needs `dm:activity:read`
     * (and `dm:system:read` for a system) beside `privacy_notice:manage` (403,
     * `details.required_permission` naming the missing grant). Both grants are
     * checked by `PrivacyNoticeService.assertDeploymentLinks` before either
     * register is read (gate E12.5 pass 1, P3-1).
     */
    'PRIVACY_NOTICE_LINK_TARGET_GRANT_REQUIRED',
    /** A linked activity or system id is not one of this organisation's (400, `field_errors`). */
    'PRIVACY_NOTICE_LINK_TARGET_NOT_FOUND',
    /**
     * A named data subject is not among the linked activities' `data_subjects`
     * (400, `field_errors`), or a `data_subject_group` notice names none.
     */
    'PRIVACY_NOTICE_AUDIENCE_INVALID',
];
//# sourceMappingURL=privacy-office.js.map